import { z } from 'zod'
import { boardLabelSchema, cuidSchema } from './schemas.js'

/**
 * Service areas: named groups of suburbs and postcodes (ADR 021).
 *
 * A job's area is read off its site's address text, not its coordinates, so
 * the server needs no geometry and an address that was never geocoded still
 * has an area. The ABS shapes the office clicks in Settings carry the same
 * names, which is what makes the two agree.
 */

export const SERVICE_AREA_PLACE_KINDS = ['SUBURB', 'POSTCODE'] as const
export type ServiceAreaPlaceKind = (typeof SERVICE_AREA_PLACE_KINDS)[number]

/**
 * "Mount  lawley (WA)" and "MOUNT LAWLEY" are the same suburb.
 *
 * Upper case, one space, no state suffix. ServiceM8 addresses arrive in every
 * case there is, and the ABS writes a name shared with another state as
 * "Kensington (WA)".
 */
export function normaliseSuburb(value: string | null | undefined): string | null {
  if (!value) return null
  const cleaned = value
    .replace(/\((?:WA|W\.A\.)\)\s*$/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()
  return cleaned || null
}

/** Four digits, or nothing. "6027 " and "WA 6027" are both 6027. */
export function normalisePostcode(value: string | null | undefined): string | null {
  if (!value) return null
  const match = /\b(\d{4})\b/.exec(value)
  return match ? match[1]! : null
}

export function normalisePlace(kind: ServiceAreaPlaceKind, value: string): string | null {
  return kind === 'SUBURB' ? normaliseSuburb(value) : normalisePostcode(value)
}

export interface ServiceAreaPlaceLike {
  areaId: string
  kind: ServiceAreaPlaceKind
  value: string
}

export interface ServiceAreaIndex {
  suburbs: ReadonlyMap<string, string>
  postcodes: ReadonlyMap<string, string>
}

export function serviceAreaIndex(places: readonly ServiceAreaPlaceLike[]): ServiceAreaIndex {
  const suburbs = new Map<string, string>()
  const postcodes = new Map<string, string>()
  for (const place of places) {
    const key = normalisePlace(place.kind, place.value)
    if (!key) continue
    ;(place.kind === 'SUBURB' ? suburbs : postcodes).set(key, place.areaId)
  }
  return { suburbs, postcodes }
}

/**
 * Which area an address is in, or null.
 *
 * The suburb wins over the postcode: a postcode can straddle two areas the
 * office drew by suburb, and the suburb is the finer of the two. The import
 * put ServiceM8's locality in `suburb` or `city`, whichever it parsed, so
 * both are read.
 */
export function serviceAreaFor(
  address:
    | { suburb?: string | null; city?: string | null; postcode?: string | null }
    | null
    | undefined,
  index: ServiceAreaIndex,
): string | null {
  if (!address) return null
  const suburb = normaliseSuburb(address.suburb) ?? normaliseSuburb(address.city)
  if (suburb) {
    const area = index.suburbs.get(suburb)
    if (area) return area
  }
  const postcode = normalisePostcode(address.postcode)
  return postcode ? (index.postcodes.get(postcode) ?? null) : null
}

export const serviceAreaPlaceSchema = z.object({
  kind: z.enum(SERVICE_AREA_PLACE_KINDS),
  value: z.string().trim().min(1).max(120),
})

/**
 * One area as the Settings screen saves it: the whole list of places and
 * technicians, which replaces what was there. A place listed here that is in
 * another area moves to this one.
 */
export const serviceAreaSaveSchema = boardLabelSchema.extend({
  id: cuidSchema.optional(),
  colour: boardLabelSchema.shape.colour.default('#2167b5'),
  places: z.array(serviceAreaPlaceSchema).max(2000),
  technicianIds: z.array(cuidSchema).max(200),
})

export type ServiceAreaSave = z.infer<typeof serviceAreaSaveSchema>
